import { Big } from 'big.js';
import BreJob from '~/tenants/bre/model/BreJob';
import BreCinematicConfig from '~/tenants/bre/performable/cinematic/BreCinematicConfig';
import { DisplayData, TenantJobLine } from '~/lib/model';
import { BreOrderType } from '~/tenants/bre/model/enums';
import { FIRST_VERSION_TIMESTAMP, ZodVersionedMetadata } from '~/lib/zod';
import { match } from 'ts-pattern';
import { BreCinematicRate } from '~/tenants/bre/performable/cinematic/BreCinematicData';
import { BRE_COPYRIGHT_PHOTO_FEE, BRE_COPYRIGHT_VIDEO_FEE } from '~/tenants/bre/data';
import { BreProviderId } from '~/tenants/bre/model/BreProvider';
import { bre_round, bre_split_photo, bre_split_video } from '~/tenants/bre/performable/common';

export default class BreCinematicJob extends BreJob<typeof BreCinematicConfig> {
  get performable(): typeof BreCinematicConfig {
    return BreCinematicConfig;
  }

  get configurable(): boolean {
    return true;
  }

  get submission() {
    return true;
  }

  get eligible(): boolean {
    return this.isOneOf(BreOrderType.RESIDENTIAL, BreOrderType.COMMERCIAL, BreOrderType.RENTAL);
  }

  get rate(): Big {
    const { type } = this.order.metadata;
    const { duration } = this.metadata;

    if (type === BreOrderType.COMMERCIAL && duration) {
      return new Big('600').times(new Big(duration).minus('1.5')).sub('399');
    }

    const under3000 = this.buyer('override_cinematic_under_3000');
    const over3000 = this.buyer('override_cinematic_over_3000');

    const sqft = Math.max(this.sqft, 1500);

    if (sqft <= 3000 && under3000) {
      return new Big(under3000);
    }

    if (sqft > 3000 && over3000) {
      return new Big(over3000);
    }

    const pricePerSqft = sqft >= 5000 ? '0.20' : '0.25';
    const sqftSubtract = sqft >= 5000 ? 1000 : 1500;

    return new Big(pricePerSqft).times(new Big(sqft).minus(sqftSubtract)).add('499');
  }

  get copyrightFee(): Big {
    const { type } = this.order.metadata;
    const { aerial_photos, aerial_upgrade } = this.metadata;
    const photos = (aerial_photos ? 5 : 0) + (aerial_upgrade ? 5 : 0);

    return new Big(BRE_COPYRIGHT_PHOTO_FEE[type]).times(photos).plus(BRE_COPYRIGHT_VIDEO_FEE[type]);
  }

  defaultValue(): ZodVersionedMetadata<(typeof BreCinematicConfig)['schema']> {
    return {
      version: FIRST_VERSION_TIMESTAMP,
    };
  }

  onsite(): number {
    const { aerial_photos, aerial_upgrade } = this.metadata;

    if (this.sqft > 0) {
      const video = match(this.sqft)
        .when(
          (sqft) => sqft < 1500,
          () => 60,
        )
        .when(
          (sqft) => sqft < 2500,
          () => 75,
        )
        .when(
          (sqft) => sqft < 3000,
          () => 85,
        )
        .when(
          (sqft) => sqft < 5000,
          () => 90,
        )
        .when(
          (sqft) => sqft < 6500,
          () => 120,
        )
        .when(
          (sqft) => sqft < 8000,
          () => 150,
        )
        .otherwise(() => 180);
      const aerial = aerial_upgrade || aerial_photos ? 30 : 0;

      return video + aerial;
    }

    return 0;
  }

  info(): DisplayData[] {
    const info = super.info();

    if (this.metadata.aerial_photos) {
      info.push({
        name: '5 Additional Aerial Photos',
        provider: true,
        value: 'Yes',
      });
    }


    if (this.metadata.aerial_upgrade) {
      info.push({
        name: 'Aerial Upgrade',
        provider: true,
        value: 'Yes',
      });
    }

    if (this.minutes > 0) {
      info.push({
        name: 'Duration',
        provider: true,
        
        value: `${this.minutes} min`,
      });
    }

    if (this.metadata.intro) {
      info.push({
        name: 'Agent Intro',
        provider: true,
        value: 'Yes',
      });
    }

    if (this.metadata.narration) {
      info.push({
        name: 'Agent Voice-Over',
        provider: true,
        value: 'Yes',
      });
    }

    if (this.metadata.kwasi) {
      info.push({
        name: 'Professional Voice-Over',
        provider: true,
        value: this.metadata.kwasi,
      });
    }

    info.push({
      name: 'Lighting',
      provider: true,
      value: this.metadata.twilight ? 'Twilight' : 'Daylight',
    });

    return info;
  }

  revenueLines(): TenantJobLine[] {
    const { aerial_photos, aerial_upgrade, intro, kwasi, narration, twilight } = this.metadata;
    const { AERIAL_PHOTOS, INTRO, KWASI, NARRATION, TWILIGHT, AERIAL_UPGRADE } =
      BreCinematicRate[this.order.metadata.type];
    const lines: TenantJobLine[] = [];

    if (this.sqft > 0) {
      lines.push({
        id: 'video',
        description: 'Cinematic Film',
        amount: bre_round(this.rate),
        discountable: true,
      });

      if (aerial_upgrade) {
        lines.push({
          id: 'upgrade',
          description: 'Cinematic Film Aerial Upgrade',
          amount: new Big(this.buyer('free_cinematic_aerial') ? 0 : AERIAL_UPGRADE),
        });
      }

      if (this.isOneOf(BreOrderType.LONG_TERM)) {
        lines.push({
          id: 'copyright',
          description: 'Copyright',
          amount: this.copyrightFee,
        });
      }
    }

    if (intro) {
      lines.push({
        id: 'intro',
        description: 'Agent Intro',
        amount: new Big(INTRO),
      });
    }

    if (narration) {
      lines.push({
        id: 'narration',
        description: 'Agent Voice-Over',
        amount: new Big(NARRATION),
      });
    }

    if (kwasi) {
      lines.push({
        id: 'kwasi',
        description: `Professional Voice-Over ${kwasi}`,
        amount: new Big(KWASI),
      });
    }

    if (twilight) {
      lines.push({
        id: 'twilight',
        description: 'Twilight Transitions',
        amount: new Big(TWILIGHT),
      });
    }

    if (aerial_photos) {
      lines.push({
        id: 'aerial_photos',
        description: '5 Additional Aerial Photos',
        amount: new Big(AERIAL_PHOTOS),
      });
    }

    return lines;
  }

  expenseLines(): TenantJobLine[] {
    const { aerial_photos, aerial_upgrade, intro, kwasi, narration, self_edited, twilight } = this.metadata;
    const { INTRO, NARRATION, AERIAL_PHOTOS, TWILIGHT, AERIAL_UPGRADE } = BreCinematicRate[this.order.metadata.type];
    const lines: TenantJobLine[] = [];

    if (this.sqft > 0) {
      lines.push({
        id: 'video',
        description: 'Cinematic Film',
        amount: bre_split_video(bre_round(this.rate), !self_edited),
      });
    }

    const upgrade = new Big(this.buyer('free_cinematic_aerial') ? 0 : AERIAL_UPGRADE);

    if (aerial_upgrade && upgrade.gt(0)) {
      lines.push({
        id: 'upgrade',
        description: 'Cinematic Film Aerial Upgrade',
        amount: bre_split_video(upgrade, !self_edited),
      });
    }

    if (intro) {
      lines.push({
        id: 'intro',
        description: 'Agent Intro',
        amount: bre_split_video(new Big(INTRO), !self_edited),
      });
    }

    if (narration) {
      lines.push({
        id: 'narration',
        description: 'Agent Voice-Over',
        amount: bre_split_video(new Big(NARRATION), !self_edited),
      });
    }

    if (twilight) {
      lines.push({
        id: 'twilight',
        description: 'Twilight Transitions',
        amount: bre_split_video(new Big(TWILIGHT), !self_edited),
      });
    }

    if (aerial_photos) {
      lines.push({
        id: 'aerial_photos',
        description: '5 Additional Aerial Photos',
        amount: bre_split_photo(new Big(AERIAL_PHOTOS)),
      });
    }

    if (kwasi) {
      lines.push({
        id: 'kwasi',
        description: `Professional Voice-Over (${kwasi})`,
        provider_id: (kwasi === 'male' ? BreProviderId.KWASI_ACHIAW : BreProviderId.ALICIA_GARCIA).toString(),
        phase: 'completed',
        amount: new Big('125'),
      });
    }

    if (this.metadata.editing_credits && this.metadata.editing_credits > 0) {
      lines.push({
        id: 'editing_credits',
        description: 'Editing Credits',
        amount: new Big(this.metadata.editing_credits).times('-1'),
      });
    }

    return lines;
  }

  get minutes(){
    if(this.metadata.duration){
      return this.metadata.duration;
    }

    if(this.sqft >= 8000){
      return 5.5;
    }

    if(this.sqft >= 6500){
      return 5;
    }

    if(this.sqft >= 5000){
      return 4.5;
    }

    if(this.sqft >= 3500){
      return 4;
    }

    if(this.sqft >= 2500){
      return 3.5;
    }

    if(this.sqft >= 1500){
      return 3;
    }

    return 2.5;
  }
}
